import { useThemeImage } from "../../hooks/useThemeImage";
import {useEffect, useState, type CSSProperties} from "react";
import TextField from "@mui/material/TextField";
import {FormControlLabel, Radio, RadioGroup} from "@mui/material";
import './styless.scss'
import {useParams} from "react-router-dom";
import {applyInvoice, getInvoiceOrderDetail, InvoiceOrderDetail} from "../../api/payment";
import {useDispatch} from "react-redux";
import {setGlobalDialogTips} from "../../store/memberSlice";
import {useMatomoTrackingPoint} from "../../hooks/useMatomoTrackingPoint";

export default function Invoice() {

    const logo = useThemeImage('logo');
    const invoiceBg = useThemeImage('invoiceBg');
    const { outTradeNo } = useParams<{ outTradeNo: string }>();
    const [detail, setDetail] = useState<InvoiceOrderDetail | null>(null);
    const [invoiceTitle, setInvoiceTitle] = useState('');
    const [taxNumber, setTaxNumber] = useState('');
    const [email, setEmail] = useState('');
    const [invoiceType, setInvoiceType] = useState<'special' | 'normal'>('normal');
    const [errors, setErrors] = useState<{ email?: string }>({});
    const [submitLoading, setSubmitLoading] = useState(false);
    const dispatch = useDispatch();
    const { trackInvoiceFormSubmit } = useMatomoTrackingPoint();

    const closeCurrentPage = () => {
        window.close();
    };

    useEffect(() => {
        if (!outTradeNo) return;
        getInvoiceOrderDetail(outTradeNo).then((res) => {
            setDetail(res);
        });
    }, [outTradeNo]);

    const formatTime = (time?: string) => {
        if (!time) return '--';
        const date = new Date(time);
        if (Number.isNaN(date.getTime())) return time;
        const yyyy = date.getFullYear();
        const mm = String(date.getMonth() + 1).padStart(2, '0');
        const dd = String(date.getDate()).padStart(2, '0');
        const hh = String(date.getHours()).padStart(2, '0');
        const mi = String(date.getMinutes()).padStart(2, '0');
        return `${yyyy}-${mm}-${dd} ${hh}:${mi}`;
    };

    const formatDesc = (description?: string) => {
        const planMap: Record<string, string> = {
            plus: '月度Plus会员',
            pro: '月度Pro会员',
            max: '月度Max会员',
            plus_year: '年度Plus会员',
            pro_year: '年度Pro会员',
            max_year: '年度Max会员',
        };

        if (!description) return '--';
        return planMap[description] || description;
    };

    const formatInvoiceStatus = (status?: string) => {
        switch (status) {
            case 'completed':
                return '已开票';
            case 'rejected':
                return '已驳回';
            case 'processing':
                return '审核中';
            case 'available':
                return '未开票';
            case 'unavailable':
                return '不可开票';
            default:
                return '--';
        }
    };

    const isEmailValid = (value: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
    const canSubmit = Boolean(invoiceTitle.trim() && taxNumber.trim() && email.trim() && !submitLoading);

    const handleSubmit = async () => {
        trackInvoiceFormSubmit({
            page_id: window.location.href,
        });
        if (!detail) return;
        if (!canSubmit) return;
        if (!isEmailValid(email.trim())) {
            setErrors({ email: '请输入正确的邮箱格式' });
            return;
        }

        setSubmitLoading(true);

        try {
            // 提交前重新获取最新开票状态，避免重复申请
            const latestDetail = await getInvoiceOrderDetail(outTradeNo || detail.out_trade_no);
            const latestStatus = latestDetail?.invoice_status;
            if (!(latestStatus === 'available' || latestStatus === 'rejected')) {
                dispatch(setGlobalDialogTips({
                    type: 'error',
                    title: '申请失败',
                    content: '该账单已开具发票，无法重复申请',
                    buttonContent: '确认',
                    cancelFun: closeCurrentPage,
                    confirmFun: closeCurrentPage,
                }));
                return;
            }

            const res = await applyInvoice({
                source_type: detail.source_type,
                payment_out_trade_no: detail.out_trade_no,
                payment_amount: detail.amount,
                payment_description: detail.description,
                payment_time: detail.success_time,
                payment_transaction_id: detail.transaction_id,
                title_type: 'enterprise',
                invoice_type: invoiceType,
                invoice_title: invoiceTitle.trim(),
                tax_number: taxNumber.trim(),
                phone_number: null,
                email: email.trim(),
            });

            if (res.code !== 200) {
                dispatch(setGlobalDialogTips({
                    type: 'error',
                    title: '申请失败',
                    content: res.message || '该账单已申请开具发票，无法重复申请',
                    buttonContent: '确认',
                    cancelFun: closeCurrentPage,
                    confirmFun: closeCurrentPage,
                }));
                return;
            }

            dispatch(setGlobalDialogTips({
                type: 'success',
                title: '发票申请已提交',
                content: '发票将在十个工作日内开具，请在账单记录页面查收',
                buttonContent: '确认',
                cancelFun: closeCurrentPage,
                confirmFun: closeCurrentPage,
            }));
        } catch (e) {
            dispatch(setGlobalDialogTips({
                type: 'error',
                title: '申请失败',
                content: '该账单已申请开具发票，无法重复申请',
                buttonContent: '确认',
                cancelFun: closeCurrentPage,
                confirmFun: closeCurrentPage,
            }));
        } finally {
            setSubmitLoading(false);
        }
    };

    return (
        <div
            className="invoice-page"
            style={{
                '--invoice-bg': `url(${invoiceBg})`,
            } as CSSProperties}
        >
            <div className="invoice-panel">
                <div className="invoice-header">
                    <img src={logo} alt="" />
                    <div className="invoice-header-title">申请开票</div>
                </div>

                <div className="invoice-info-card">
                    <div className="invoice-row">
                        <div className="invoice-label">Krene 账号</div>
                        <div className="invoice-value">{detail?.uuid || '--'}</div>
                    </div>
                    <div className="invoice-row">
                        <div className="invoice-label">消费金额</div>
                        <div className="invoice-value">¥{detail?.amount || '--'}</div>
                    </div>
                    <div className="invoice-row">
                        <div className="invoice-label">消费项目</div>
                        <div className="invoice-value">{formatDesc(detail?.description)}</div>
                    </div>
                    <div className="invoice-row">
                        <div className="invoice-label">消费时间</div>
                        <div className="invoice-value">{formatTime(detail?.success_time)}</div>
                    </div>
                    <div className="invoice-row">
                        <div className="invoice-label">交易商户订单号</div>
                        <div className="invoice-value">{detail?.out_trade_no || outTradeNo || '--'}</div>
                    </div>
                    <div className="invoice-row">
                        <div className="invoice-label">开票状态</div>
                        <div className="invoice-value">{formatInvoiceStatus(detail?.invoice_status)}</div>
                    </div>
                </div>

                <div className="invoice-second-card" style={{paddingTop: '20px', boxSizing: 'border-box'}}>
                    <div className="invoice-row invoice-row-input">
                        <div className="invoice-label">发票类型</div>
                        <div className="invoice-input-wrap">
                            <RadioGroup
                                row
                                value={invoiceType}
                                onChange={(e) => setInvoiceType(e.target.value as 'special' | 'normal')}
                            >
                                <FormControlLabel value="normal" control={<Radio />} label="普票" />
                                <FormControlLabel value="special" control={<Radio />} label="专票" />
                            </RadioGroup>
                        </div>
                    </div>
                    <div className="invoice-row invoice-row-input">
                        <div className="invoice-label">发票抬头</div>
                        <div className="invoice-input-wrap">
                            <TextField
                                placeholder="请填写单位名称（必填）"
                                variant="outlined"
                                size="small"
                                value={invoiceTitle}
                                onChange={(e) => {
                                    setInvoiceTitle(e.target.value);
                                }}
                                sx={{
                                    width: {xs: 385, sm: 490},
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: '10px',
                                    },
                                    '& .MuiOutlinedInput-notchedOutline': {
                                        borderColor: 'var(--c-border)',
                                        borderRadius: '10px',
                                    },
                                    '&:hover .MuiOutlinedInput-notchedOutline': {
                                        borderColor: 'var(--c-border)',
                                        borderRadius: '10px',
                                    },
                                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                        borderColor: 'var(--c-border)',
                                        borderRadius: '10px',
                                    },
                                }}
                            />
                        </div>
                    </div>
                    <div className="invoice-row invoice-row-input">
                        <div className="invoice-label">税号</div>
                        <div className="invoice-input-wrap">
                            <TextField
                                placeholder="请填写税号（必填）"
                                variant="outlined"
                                size="small"
                                value={taxNumber}
                                onChange={(e) => {
                                    setTaxNumber(e.target.value);
                                }}
                                sx={{
                                    width: {xs: 385, sm: 490},
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: '10px',
                                    },
                                    '& .MuiOutlinedInput-notchedOutline': {
                                        borderColor: 'var(--c-border)',
                                        borderRadius: '10px',
                                    },
                                    '&:hover .MuiOutlinedInput-notchedOutline': {
                                        borderColor: 'var(--c-border)',
                                        borderRadius: '10px',
                                    },
                                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                        borderColor: 'var(--c-border)',
                                        borderRadius: '10px',
                                    },
                                }}
                            />
                        </div>
                    </div>
                    <div className="invoice-row invoice-row-input">
                        <div className="invoice-label">邮箱</div>
                        <div className="invoice-input-wrap">
                            <TextField
                                placeholder="请填写邮箱（必填）"
                                variant="outlined"
                                size="small"
                                value={email}
                                onChange={(e) => {
                                    setEmail(e.target.value);
                                    if (errors.email) setErrors((prev) => ({ ...prev, email: undefined }));
                                }}
                                sx={{
                                    width: {xs: 385, sm: 490},
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: '10px',
                                    },
                                    '& .MuiOutlinedInput-notchedOutline': {
                                        borderColor: 'var(--c-border)',
                                        borderRadius: '10px',
                                    },
                                    '&:hover .MuiOutlinedInput-notchedOutline': {
                                        borderColor: 'var(--c-border)',
                                        borderRadius: '10px',
                                    },
                                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                        borderColor: 'var(--c-border)',
                                        borderRadius: '10px',
                                    },
                                }}
                            />
                            {errors.email ? <div className="invoice-input-error">{errors.email}</div> : null}
                        </div>
                    </div>
                    <div
                        className="invoice-submit-btn"
                        style={{
                            backgroundColor: canSubmit ? 'var(--c-brand)' : 'var(--c-border)',
                            cursor: canSubmit ? 'pointer' : 'not-allowed'
                        }}
                        onClick={handleSubmit}
                    >
                        {submitLoading ? '提交中...' : '提交申请'}
                    </div>
                    <div className="invoice-submit-hint">发票将在十个工作日内开具，请在账单记录页面查收</div>
                </div>
            </div>
        </div>
    );
}
